"use client";

import { RefreshCw, WifiOff } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ShellSource } from "../lib/use-connectivity";

type PlaygroundConnectivityChipProps = {
  online: boolean;
  shellSource: ShellSource;
  /** Reload for the latest shell; the owner flushes the draft first. */
  onReload: () => void;
};

/**
 * The local-only state, said out loud (`PG-PWA01`, `docs/24` §10.2).
 *
 * Two states, never one colour alone (`docs/03` §12): offline, where the
 * editor and the draft keep working and the online-only actions say why they
 * are off; and a cached copy of the shell served while the network is back,
 * where the one action is a reload. Online from the network renders nothing.
 */
export function PlaygroundConnectivityChip({
  online,
  shellSource,
  onReload,
}: PlaygroundConnectivityChipProps) {
  const t = useTranslations("playground");

  if (!online) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t("connectivityStatusLabel")}
        className="flex min-h-[36px] items-center gap-2 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-1.5 text-xs text-amber-100"
      >
        <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>{t("offlineChip")}</span>
      </div>
    );
  }

  if (shellSource === "cache") {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label={t("connectivityStatusLabel")}
        className="flex min-h-[36px] flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/80"
      >
        <span className="inline-flex items-center gap-2">
          <RefreshCw className="h-3.5 w-3.5 shrink-0 text-[#FFAB8F]" aria-hidden />
          <span>{t("cachedCopyChip")}</span>
        </span>
        <button
          type="button"
          onClick={onReload}
          className="inline-flex min-h-[28px] items-center rounded-md border border-[#E94D1D]/50 bg-[#E94D1D]/15 px-2.5 text-xs font-semibold text-[#FFAB8F] transition hover:bg-[#E94D1D]/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E94D1D]/45 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0C]"
        >
          {t("cachedCopyReload")}
        </button>
      </div>
    );
  }

  return null;
}
